package com.dotdrives.model

import kotlin.time.Instant

/** Display labels can evolve without changing the immutable submission/export key. */
val FormField.displayLabel: String
    get() = label?.trim()?.takeIf(String::isNotEmpty) ?: when (key) {
        "email" -> "Email address"
        "name", "full_name" -> "Full name"
        else -> key.replace('_', ' ').replaceFirstChar { it.uppercase() }
    }

fun parseFormGoal(value: String): Result<Double?> = runCatching {
    val text = value.trim()
    if (text.isEmpty()) null else {
        // Anchor alternatives: Kotlin/JS matches() otherwise accepts the first prefix match
        // before checking its length, unlike JVM matching which backtracks to the full value.
        require(Regex("^(?:[0-9]+|[1-9][0-9]{0,2}(?:,[0-9]{3})+)(?:\\.[0-9]{1,2})?$").matches(text)) {
            "Enter a positive goal, such as 25,000 or 25,000.50."
        }
        val amount = text.replace(",", "").toDouble()
        require(amount.isFinite() && amount > 0 && amount <= 9_999_999_999.99) {
            "Enter a positive goal below 10 billion, or leave it blank."
        }
        amount
    }
}

/** UTC datetime-local values or explicit ISO offsets; a date alone closes at that UTC day's end. */
fun parseFormCloseAt(value: String): Result<Instant?> = runCatching {
    val text = value.trim()
    if (text.isEmpty()) null else try {
        val iso = when {
            Regex("[0-9]{4}-[0-9]{2}-[0-9]{2}").matches(text) -> "${text}T23:59:59.999999Z"
            Regex("[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}").matches(text) -> "${text}:00Z"
            Regex("[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(?:\\.[0-9]{1,9})?").matches(text) -> "${text}Z"
            else -> text
        }
        Instant.parse(iso)
    } catch (_: Exception) { throw IllegalArgumentException("Enter a valid closing date and time in UTC, or leave it blank.") }
}

/** Uploads use a fresh object URL; only saving the draft attaches it to the public form. */
data class FormLogoUpload(val url: String, val fileName: String, val mimeType: String, val sizeBytes: Int)

const val MAX_FORM_LOGO_BYTES = 2 * 1024 * 1024
val formLogoMimeTypes = setOf("image/png", "image/jpeg", "image/webp")

fun validateFormLogo(name: String, mime: String, bytes: ByteArray) {
    require(name.isNotBlank() && name.length <= 255) { "Choose a logo with a file name of 1–255 characters." }
    require(bytes.isNotEmpty() && bytes.size <= MAX_FORM_LOGO_BYTES) { "Choose a logo no larger than 2 MiB." }
    require(mime in formLogoMimeTypes) { "Choose a PNG, JPG or WebP logo. SVG files are not supported." }
    fun byte(index: Int) = bytes.getOrNull(index)?.toInt()?.and(255)
    val valid = when (mime) {
        "image/png" -> bytes.size >= 24 && listOf(137,80,78,71,13,10,26,10).indices.all { byte(it) == listOf(137,80,78,71,13,10,26,10)[it] } && bytes.copyOfRange(12,16).decodeToString() == "IHDR"
        "image/jpeg" -> bytes.size >= 4 && byte(0) == 255 && byte(1) == 216 && byte(2) == 255 && byte(bytes.lastIndex-1) == 255 && byte(bytes.lastIndex) == 217
        else -> bytes.size >= 16 && bytes.copyOfRange(0,4).decodeToString() == "RIFF" && bytes.copyOfRange(8,12).decodeToString() == "WEBP" && bytes.copyOfRange(12,15).decodeToString() == "VP8"
    }
    require(valid) { "The file does not match its PNG, JPG or WebP image type." }
}
